import React, { useCallback, useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import useApi from '../../../hooks/useApi'
import Icon from '../../../components/icon'
import EzTooltip from '../../../components/tooltip'
import styles from './volunteerList.module.scss'

export default function FollowButton({ volunteer, isCurrentUser, onUpdate }) {
  const { t } = useTranslation()
  const { userFollow, userUnfollow } = useApi()

  const [optimistic, setOptimistic] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setOptimistic(null)
  }, [volunteer.user?.id, volunteer.user?.current_user_follows])

  const isFollowing =
    optimistic ?? volunteer.user?.current_user_follows ?? false

  const handleFollow = useCallback(async () => {
    if (isLoading || isCurrentUser || !volunteer?.user?.id) return
    setIsLoading(true)
    const newFollowingState = !isFollowing
    setOptimistic(newFollowingState)

    try {
      const response = isFollowing
        ? await userUnfollow({ id: volunteer.user.id })
        : await userFollow({ id: volunteer.user.id })

      const status = response?.status ?? response?.response?.status
      const success = status == null || (status >= 200 && status < 300)

      if (success) {
        if (onUpdate) {
          await new Promise(resolve => setTimeout(resolve, 300))
          await onUpdate()
        }
      } else {
        setOptimistic(null)
      }
    } catch (error) {
      console.error('Error toggling follow:', error)
      setOptimistic(null)
    } finally {
      setIsLoading(false)
    }
  }, [
    volunteer?.user?.id,
    isFollowing,
    isCurrentUser,
    isLoading,
    onUpdate,
    userFollow,
    userUnfollow,
  ])

  const backgroundColorClass = useMemo(
    () => (isFollowing ? styles.following : styles.notFollowing),
    [isFollowing],
  )

  if (
    isCurrentUser ||
    !volunteer?.user?.id ||
    volunteer.user?.current_user_blocks
  ) {
    return null
  }

  return (
    <span className={styles.actionButtonWrapper}>
      <EzTooltip
        id={`follow-tooltip-${volunteer.id}`}
        innerContent={
          isFollowing
            ? t('buttons.unfollow', 'Unfollow')
            : t('buttons.follow', 'Follow')
        }
        placement="top"
        outerContent={
          <button
            className={`${styles.actionButton} ${backgroundColorClass}`}
            onClick={handleFollow}
            disabled={isLoading}
            type="button"
            aria-label={
              isFollowing
                ? t('buttons.unfollow', 'Unfollow')
                : t('buttons.follow', 'Follow')
            }
          >
            <Icon name="user-plus" />
          </button>
        }
      />
    </span>
  )
}
